import { FC, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

import { Checkbox, Field, SelectDropdown, Slider } from '@makyoapp/makyo-ui'

import { useActiveDevice } from '@/hooks/useActiveDevice'
import {
  DEFAULT_MASK_VALUES,
  SectionBackgroundGroupProps,
  SectionBackgroundGroupValues,
} from '@/shared/builder/default/section.default'

interface MaskBackgroundSelectProps {
  layer: SectionBackgroundGroupProps
  handleUpdate: (id: number, newValues: any) => Promise<void>
}

export const MaskBackgroundSelect: FC<MaskBackgroundSelectProps> = ({ handleUpdate, layer }) => {
  const [isApplying, setIsApplying] = useState(false)

  const { t } = useTranslation()

  const [device] = useActiveDevice()

  const currentLayer = (layer.values as Record<string, Partial<SectionBackgroundGroupValues>>)[device]

  const maskOptions = [
    { value: '#', label: 'No Mask' },
    { value: '/mask/arrow-down.svg', label: 'Arrow Down' },
    { value: '/mask/bar-chart.svg', label: 'Bar Chart' },
    { value: '/mask/blob2.svg', label: 'Blob 2' },
    { value: '/mask/blood-drop.svg', label: 'Blood Drop' },
    { value: '/mask/capsules.svg', label: 'Capsules' },
    { value: '/mask/chart.svg', label: 'Chart' },
    { value: '/mask/cloud.svg', label: 'Cloud' },
    { value: '/mask/cone.svg', label: 'Cone' },
    { value: '/mask/cube.svg', label: 'Cube' },
    { value: '/mask/diamond.svg', label: 'Diamond' },
    { value: '/mask/dice.svg', label: 'Dice' },
    { value: '/mask/distort.svg', label: 'Distort' },
    { value: '/mask/down-arrow.svg', label: 'Down Arrow' },
    { value: '/mask/flower.svg', label: 'Flower' },
    { value: '/mask/flowers.svg', label: 'Flowers' },
    { value: '/mask/focus.svg', label: 'Focus' },
    { value: '/mask/form.svg', label: 'Form' },
    { value: '/mask/geometry.svg', label: 'Geometry' },
    { value: '/mask/globe.svg', label: 'Globe' },
    { value: '/mask/graphs.svg', label: 'Graphs' },
    { value: '/mask/half-circle.svg', label: 'Half Circle' },
    { value: '/mask/harmony.svg', label: 'Harmony' },
    { value: '/mask/heart.svg', label: 'Heart' },
    { value: '/mask/hexagonal.svg', label: 'Hexagonal' },
    { value: '/mask/increase.svg', label: 'Increase' },
    { value: '/mask/kidney.svg', label: 'Kidney' },
    { value: '/mask/leave.svg', label: 'Leave' },
    { value: '/mask/levels.svg', label: 'Levels' },
    { value: '/mask/lotus.svg', label: 'Lotus' },
    { value: '/mask/merge.svg', label: 'Merge' },
    { value: '/mask/mind-map.svg', label: 'Mind Map' },
    { value: '/mask/mountain.svg', label: 'Mountain' },
    { value: '/mask/non-chlorine.svg', label: 'Non Chlorine' },
    { value: '/mask/paint.svg', label: 'Paint' },
    { value: '/mask/paper.svg', label: 'Paper' },
    { value: '/mask/pattern.svg', label: 'Pattern' },
    { value: '/mask/pills.svg', label: 'Pills' },
    { value: '/mask/priority.svg', label: 'Priority' },
    { value: '/mask/rectangle.svg', label: 'Rectangle' },
    { value: '/mask/restore.svg', label: 'Restore' },
    { value: '/mask/rhombus.svg', label: 'Rhombus' },
    { value: '/mask/sea-arch.svg', label: 'Sea Arch' },
    { value: '/mask/speech-bubble.svg', label: 'Speech Bubble' },
    { value: '/mask/stain.svg', label: 'Stain' },
    { value: '/mask/stamp.svg', label: 'Stamp' },
    { value: '/mask/star.svg', label: 'Star' },
    { value: '/mask/statistics.svg', label: 'Statistics' },
    { value: '/mask/virus.svg', label: 'Virus' },
    { value: '/mask/wave.svg', label: 'Wave' },
    { value: '/mask/waves.svg', label: 'Waves' },
  ]

  const handleUpdateMask = useCallback(
    (e: string | number | number[] | boolean, key: string) => {
      const layerData = currentLayer || DEFAULT_MASK_VALUES
      handleUpdate(layer.id, {
        ...layer.values,
        [device]: {
          ...layerData,
          [key]: e,
        },
      })
    },
    [handleUpdate, layer.id, layer.values, device, currentLayer]
  )

  const handleApplyChanges = useCallback(() => {
    setIsApplying(true)
    const devices = ['phone', 'phone-landscape', 'tablet', 'tablet-landscape', 'tv', 'desktop']
    const payload = Object.fromEntries(devices.map((device) => [device, currentLayer]))
    handleUpdate(layer.id, payload)

    const timeout = setTimeout(() => {
      setIsApplying(false)
    }, 1000)
    return () => clearTimeout(timeout)
  }, [currentLayer, handleUpdate, layer.id])

  return (
    <div className='space-y-4'>
      <SelectDropdown
        withSearch
        value={currentLayer?.type || '#'}
        onChange={(e) => handleUpdateMask(e, 'type')}
        renderOption={(option) => {
          const getOption = maskOptions.find((o) => o.value === option)
          return (
            <div className='flex items-center gap-2'>
              {getOption?.value && getOption?.value !== '#' && (
                <img src={option as string} alt={getOption?.label} className='w-8 h-8' />
              )}
              <span>{getOption?.label}</span>
            </div>
          )
        }}
        options={maskOptions.map((o) => o.value)}
      />
      {currentLayer?.type && currentLayer?.type !== '#' && (
        <>
          <Field isVertical>
            <span className='text-left'>Size</span>
            <Slider
              min={0}
              max={200}
              step={1}
              values={[(currentLayer.size as number) ?? 50]}
              onChange={(e) => handleUpdateMask(e, 'size')}
              showLabel
            />
          </Field>
          <Field isVertical>
            <span className='text-left'>X Pos</span>
            <Slider
              min={0}
              max={100}
              step={1}
              values={[(currentLayer.xpos as number) ?? 0]}
              onChange={(e) => handleUpdateMask(e, 'xpos')}
              showLabel
            />
          </Field>
          <Field isVertical>
            <span className='text-left'>Y Pos</span>
            <Slider
              min={0}
              max={100}
              step={1}
              values={[(currentLayer.ypos as number) ?? 0]}
              onChange={(e) => handleUpdateMask(e, 'ypos')}
              showLabel
            />
          </Field>
          <Field isVertical>
            <span className='text-left'>Rotate</span>
            <Slider
              min={-180}
              max={180}
              step={1}
              values={[(currentLayer.rotate as number) ?? 0]}
              onChange={(e) => handleUpdateMask(e, 'rotate')}
              showLabel
            />
          </Field>
          <Field isVertical>
            <span className='text-left'>Opacity</span>
            <Slider
              min={0}
              max={1}
              step={0.05}
              values={[(currentLayer.opacity as number) ?? 1]}
              onChange={(e) => handleUpdateMask(e, 'opacity')}
              showLabel
            />
          </Field>
          <Field isVertical>
            <Checkbox
              label='Invert Mask'
              checked={(currentLayer.inverted as boolean) ?? false}
              onClick={(e) => handleUpdateMask(e, 'inverted')}
            />
          </Field>
          <button
            className={clsx(
              'text-gray-100 text-xs py-2 px-3 rounded-sm w-full transition-colors',
              isApplying
                ? 'bg-primary-700 dark:bg-primary-700/85 cursor-wait'
                : 'bg-primary-500 dark:bg-primary-500/85 hover:bg-primary-600'
            )}
            onClick={handleApplyChanges}
            disabled={isApplying}
          >
            {isApplying ? t('Saving changes...') : t('Save changes across all screen sizes')}
          </button>
        </>
      )}
    </div>
  )
}
